import { Either, Left, Right } from 'fp-ts/lib/Either'

import { CartItem, NewCartItem } from '@/models/cart/cart-item'
import { CartItemRepository } from '@/models/cart/repository/cart-item-repository'
import { CartService, CartServiceFlyweightStore, CartServiceStore } from '@spa/services/api/cart'

export class EngineCartRepository implements CartItemRepository {
  constructor(private readonly cartServiceStore: CartServiceStore = new CartServiceFlyweightStore()) { }

  async add(item: NewCartItem): Promise<Either<Error, null>> {
    const result = await this.cartServiceFor(item)
      .add(
        item.skuId,
        item.unitCount,
        item.unitPrice,
        item.isWithPaidMembershipPrice
      )

    return result.map(() => null)
  }

  async remove(cartItem: CartItem): Promise<Either<Error, null>> {
    return (await this.cartServiceFor(cartItem).remove(cartItem.id)).map(() => null)
  }
  async removeSome(items: CartItem[]): Promise<Either<Error[], null>> {
    const results = await Promise.all(items.map(i => this.remove(i)))
    const errors = results.reduce<Error[]>(
      (prevErrors, result) => result.fold(
        error => [...prevErrors, error],
        () => prevErrors
      ),
      []
    )

    return errors.length === 0 ? new Right(null) : new Left(errors)
  }

  async changeCount(cartItem: CartItem, count: number): Promise<Either<Error, null>> {
    if (count <= 0) {
      // :pensive:
      // 本来、こうしたガードはドメイン層で（値オブジェクト等）で担保したい。
      // 現状はプレーンなオブジェクトが使われており、かつその整合性もオブジェクト自身で担保できないことから、
      // 実行の瞬間まで値の範囲を担保することが難しい。
      // そのため、制約のインフラ層への流出を承知の上で、ここで水際のガードを用意している。
      throw new Error(`cart item count must be > 0, but specified is ${count}`)
    }

    return (await this.cartServiceFor(cartItem).updateUnitCount(cartItem.id, count)).map(() => null)
  }

  private cartServiceFor(item: { brandEnglishName: string | null }): CartService {
    const brand = item.brandEnglishName
    if (!brand) {
      throw new Error(`brand name must be specified, but actual is empty`)
    }

    return this.cartServiceStore.getServiceForBrand(brand)
  }
}

export class InvalidCartItemError extends Error { }
